import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import type { Option } from 'modules/core/components/Popover/Content'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'

import TenurePeriod from 'modules/timeoff/lib/TenurePeriod'
import type { ITenurePeriod as PeriodAttributes } from 'modules/timeoff/lib/TenurePeriod'

interface Props {
  payload: PeriodAttributes
  onEdit: (period: PeriodAttributes) => void
  onDelete: (period: PeriodAttributes) => void
  baseAllowance: number
  isHourly: boolean
}

const PeriodItem = ({ payload, onEdit, onDelete, baseAllowance, isHourly }: Props) => {
  const period = new TenurePeriod(payload)

  const label = period.isInYears
    ? i18n.tp('time_off.allowances.page.tenure_periods.item.length_in_years', {
        count: +period.periodLength,
      })
    : i18n.tp('time_off.allowances.page.tenure_periods.item.length_in_months', {
        count: +period.periodLength,
      })

  const adjustment = period.adjustment ? period.adjustment : 0
  const count = baseAllowance + adjustment

  const totalSubtitle = isHourly
    ? i18n.tp('time_off.allowances.page.tenure_periods.item.total_adjustment_in_hours', { count })
    : i18n.tp('time_off.allowances.page.tenure_periods.item.total_adjustment_in_days', { count })

  const totalAdjustmentDetails = `(${baseAllowance} + ${adjustment}) ${baseAllowance + adjustment}`

  const maxBalanceCapSubtitle = isHourly
    ? i18n.t('time_off.allowances.page.tenure_periods.item.max_balance_hours')
    : i18n.t('time_off.allowances.page.tenure_periods.item.max_balance_days')

  const tenureMaxBalanceCap = period.maxBalanceCap
  const isFixed = period.isFixed
  const isTimeWorkedBased = period.isTimeWorkedBased

  const hoursAccrued = period.timeWorkedBasedHoursAccrued
  const perHour = period.timeWorkedBasedPerHoursWorked

  const accruedHoursSubtitle = i18n.t('time_off.allowances.page.tenure_periods.item.accrued_hours')
  const perWorkedHoursSubtitle = i18n.t(
    'time_off.allowances.page.tenure_periods.item.per_worked_hours'
  )

  const options: Option[] = [
    {
      title: i18n.t('time_off.allowances.page.button.edit_tenure_period'),
      onClick: () => onEdit(payload),
    },
    {
      title: i18n.t('time_off.allowances.page.button.delete_tenure_period'),
      onClick: () => onDelete(payload),
      type: 'wrong',
    },
  ]

  return (
    <Box
      flexGrow
      paddingX="s24"
      paddingY="s16"
      border={{
        top: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
    >
      <Box flexGrow flexDirection="row" justifyContent="spaceBetween" paddingBottom="s8">
        <Text.Header2>{label}</Text.Header2>
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <Button type="secondary" iconName="ellipsis" />
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="end">
            {options.map((option) => {
              return (
                <PopoverMenu.TouchableOption
                  key={option.title}
                  onClick={option.onClick}
                  title={option.title || ''}
                />
              )
            })}
          </PopoverMenu.Content>
        </PopoverMenu>
      </Box>

      {isFixed && (
        <Box flexGrow flexDirection="row" justifyContent="spaceBetween">
          <Text.Normal color="grey600">{totalSubtitle}</Text.Normal>
          <Text.Normal color="grey600">{totalAdjustmentDetails}</Text.Normal>
        </Box>
      )}
      {isTimeWorkedBased && (
        <div>
          <Box flexGrow flexDirection="row" justifyContent="spaceBetween">
            <Text.Normal color="grey600">{accruedHoursSubtitle}</Text.Normal>
            <Text.Normal color="grey600">{hoursAccrued}</Text.Normal>
          </Box>
          <Box flexGrow flexDirection="row" justifyContent="spaceBetween">
            <Text.Normal color="grey600">{perWorkedHoursSubtitle}</Text.Normal>
            <Text.Normal color="grey600">{perHour}</Text.Normal>
          </Box>
        </div>
      )}
      {tenureMaxBalanceCap && (
        <Box flexGrow flexDirection="row" justifyContent="spaceBetween">
          <Text.Normal color="grey600">{maxBalanceCapSubtitle}</Text.Normal>
          <Text.Normal color="grey600">{tenureMaxBalanceCap}</Text.Normal>
        </Box>
      )}
    </Box>
  )
}

export default observer(PeriodItem)
